import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiEdit2, FiCheck, FiEye, FiShoppingCart, FiStar } from 'react-icons/fi';
import api from '@/services/api';
import { formatCurrency } from '@/utils/formatters';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import ProductCard from '@/components/cards/ProductCard';

const ProductPreview = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProduct();
  }, [id]);

  const loadProduct = async () => {
    try {
      const response = await api.get(`/products/${id}`);
      setProduct(response.data);
      
      const storeResponse = await api.get(`/stores/${response.data.store_id}`);
      setStore(storeResponse.data);
    } catch (error) {
      console.error('Error loading product:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">Produto não encontrado</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/lojista/produtos')}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <FiArrowLeft className="text-xl" />
            </button>
            <h1 className="font-semibold text-gray-800">Preview do Produto</h1>
          </div>
          <button
            onClick={() => navigate(`/lojista/produto/${id}`)}
            className="flex items-center gap-2 px-4 py-2 text-[#DC143C] hover:bg-red-50 rounded-lg transition-colors"
          >
            <FiEdit2 size={18} />
            <span>Editar</span>
          </button>
        </div>
      </header>

      {/* Success Banner */}
      <div className="bg-green-500 text-white px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center gap-2">
          <FiCheck className="text-xl" />
          <span className="font-medium">Produto salvo com sucesso!</span>
          <span className="text-green-100">Veja como ele aparece para os clientes</span>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Preview Card Section */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4 text-gray-600">
            <FiEye />
            <h2 className="font-semibold">Como aparece na vitrine</h2>
          </div>
          
          <div className="max-w-xs mx-auto">
            <ProductCard
              product={{
                ...product,
                store_name: store?.name || 'Sua Loja'
              }}
              onClick={() => {}}
            />
          </div>
        </div>

        {/* Full Preview Section */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="p-4 border-b bg-gray-50">
            <h2 className="font-semibold text-gray-700">Página do produto (visão do cliente)</h2>
          </div>
          
          {/* Product Image */}
          <div className="bg-white">
            {product.photos && product.photos.length > 0 ? (
              <img
                src={typeof product.photos[0] === 'object' ? product.photos[0].url : product.photos[0]}
                alt={product.name}
                className="w-full h-72 object-contain"
              />
            ) : (
              <div className="w-full h-72 bg-gray-100 flex items-center justify-center">
                <span className="text-gray-400">Sem foto</span>
              </div>
            )}
            
            {/* Thumbnails */}
            {product.photos && product.photos.length > 1 && (
              <div className="flex gap-2 p-4 overflow-x-auto">
                {product.photos.map((photo, index) => (
                  <div key={index} className="w-16 h-16 rounded-lg overflow-hidden border-2 border-gray-200 flex-shrink-0">
                    <img
                      src={typeof photo === 'object' ? photo.url : photo}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="p-4 space-y-4">
            <div className="flex items-start justify-between">
              <div>
                <span className={`inline-block px-2 py-1 text-xs rounded-full mb-2 ${
                  product.condition === 'novo' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                }`}>
                  {product.condition === 'novo' ? 'Novo' : 'Usado'}
                </span>
                <h1 className="text-xl font-bold text-gray-800">{product.name}</h1>
              </div>
              <span className="text-2xl font-bold text-[#DC143C]">
                {formatCurrency(product.price)}
              </span>
            </div>
            
            <p className="text-gray-600">{product.description}</p>
            
            <div className="flex items-center gap-4 text-sm">
              <span className={`px-3 py-1 rounded-full ${
                product.stock > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
              }`}>
                {product.stock > 0 ? `${product.stock} em estoque` : 'Esgotado'}
              </span>
              <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-600">
                {product.category}
              </span>
            </div>

            {/* Store Info */}
            {store && (
              <div className="pt-4 border-t">
                <p className="text-sm text-gray-500 mb-2">Vendido por</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#DC143C] rounded-full flex items-center justify-center text-white font-bold">
                    {store.name[0]}
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">{store.name}</p>
                    {store.rating && (
                      <div className="flex items-center gap-1 text-sm text-gray-500">
                        <FiStar className="text-yellow-400 fill-current" size={14} />
                        <span>{store.rating.toFixed(1)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Simulated Add to Cart Button */}
            <div className="pt-4">
              <button
                disabled
                className="w-full py-4 bg-gray-300 text-gray-500 rounded-xl font-semibold flex items-center justify-center gap-2 cursor-not-allowed"
              >
                <FiShoppingCart />
                Adicionar ao Carrinho (preview)
              </button>
              <p className="text-xs text-gray-400 text-center mt-2">
                Este é um preview. Os clientes verão o botão ativo.
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4">
          <button
            onClick={() => navigate('/lojista/produtos')}
            className="flex-1 py-4 bg-white border border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-colors"
          >
            Ver Todos os Produtos
          </button>
          <button
            onClick={() => navigate('/lojista/produto/novo')}
            className="flex-1 py-4 bg-[#DC143C] text-white rounded-xl font-semibold hover:bg-[#B01030] transition-colors"
          >
            Cadastrar Outro
          </button>
        </div>
      </main>
    </div>
  );
};

export default ProductPreview;
